import { Pipe, PipeTransform } from '@angular/core';
import { DecimalPipe } from '@angular/common';

export type StatLabelFormat = 'number' | 'pct' | 'thousands';

/**
 * Renders a stat value, falling back to `--` (or `--%`) while data is loading or
 * when the value is missing. Use across the account so empty/loading stats never
 * flash a misleading `0` (or a blank) while a request is in flight.
 *
 * `format` accepts the keywords below, or any Angular DecimalPipe `digitsInfo`
 * string for arbitrary precision (the canonical `--` mechanism — supersedes the
 * old `ftNumberFallback` pipe):
 *
 *   {{ count | statLabel: loading : 'thousands' }}   → "1,234" / "--"
 *   {{ pct   | statLabel: loading : 'pct' }}          → "37%" / "<1%" / "0%" / "--%"
 *   {{ value | statLabel: loading }}                  → "42" / "--"
 *   {{ value | statLabel: loading : '1.0-3' }}        → "1.234" / "--"  (DecimalPipe)
 */
@Pipe({
    name: 'statLabel',
    standalone: false
})
export class StatLabelPipe implements PipeTransform {
    private readonly decimal = new DecimalPipe('en-US');

    transform(
        value: number | null | undefined,
        loading = false,
        format: StatLabelFormat | string = 'number',
    ): string {
        const placeholder = format === 'pct' ? '--%' : '--';
        if (loading || value == null || isNaN(value)) return placeholder;

        switch (format) {
            case 'pct':
                if (value <= 0) return '0%';
                if (value < 1) return '<1%';
                return Math.round(value) + '%';
            case 'thousands':
                return value.toLocaleString('en-US');
            case 'number':
                return value.toString();
            default:
                // Any other string is treated as an Angular DecimalPipe digitsInfo (e.g. '1.0-3').
                return this.decimal.transform(value, format) ?? placeholder;
        }
    }
}
